import { useState, useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { db } from "../firebase";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  orderBy,
  limit,
} from "firebase/firestore";

gsap.registerPlugin(ScrollTrigger);

const US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@300;400;500&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { background: #040008; overflow-x: hidden; }
  input, select, textarea { font-family: inherit; }
`;

// ── Keyframes
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position:  200% center; }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50%       { opacity: 0.9; transform: scale(1.06); }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-10px); }
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const checkPop = keyframes`
  0%   { transform: scale(0) rotate(-10deg); opacity: 0; }
  60%  { transform: scale(1.15) rotate(3deg); }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;

const countUp = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

// ── Layout
const Page = styled.div`
  min-height: 100vh;
  background: #040008;
  position: relative;
  overflow: hidden;
`;

const Grain = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  opacity: 0.03;
  z-index: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const Orb = styled.div`
  position: fixed;
  border-radius: 50%;
  filter: blur(${({ blur }) => blur || "120px"});
  pointer-events: none;
  opacity: ${({ op }) => op || 0.15};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  background: ${({ c }) => c};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  bottom: ${({ bottom }) => bottom};
  animation: ${pulse} ${({ dur }) => dur || "5s"} ease-in-out infinite;
  animation-delay: ${({ del }) => del || "0s"};
`;

// ── Hero
const Hero = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: clamp(5rem, 10vh, 8rem) clamp(1.5rem, 5vw, 4rem);
  position: relative;
  z-index: 2;
  text-align: center;
`;

const HeroEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: "DM Sans", sans-serif;
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: #a855f7;
  margin-bottom: 1.8rem;
  opacity: 0;
  transform: translateY(16px);

  &::before,
  &::after {
    content: "";
    width: 20px;
    height: 1px;
    background: rgba(168, 85, 247, 0.5);
  }
`;

const HeroTitle = styled.h1`
  font-family: "Playfair Display", serif;
  font-size: clamp(2.8rem, 7vw, 7rem);
  font-weight: 700;
  line-height: 1;
  color: #fff;
  margin-bottom: 1.5rem;
  max-width: 800px;
  opacity: 0;
  transform: translateY(30px);

  em {
    font-style: italic;
    font-weight: 400;
    background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
    background-size: 200% auto;
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    animation: ${shimmer} 5s linear infinite;
  }
`;

const HeroSub = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(1rem, 1.5vw, 1.15rem);
  font-weight: 300;
  color: rgba(255, 255, 255, 0.42);
  line-height: 1.8;
  max-width: 500px;
  margin: 0 auto 3rem;
  opacity: 0;
  transform: translateY(20px);
`;

// Live counter badge
const CounterBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(168, 85, 247, 0.1);
  border: 1px solid rgba(168, 85, 247, 0.25);
  border-radius: 100px;
  padding: 8px 18px;
  margin-bottom: 3rem;
  opacity: 0;
  transform: translateY(14px);
`;

const CounterDot = styled.div`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #e879f9;
  box-shadow: 0 0 10px #e879f9;
  animation: ${pulse} 1.5s ease-in-out infinite;
`;

const CounterText = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.78rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.65);

  strong {
    color: #c084fc;
    font-weight: 600;
    animation: ${countUp} 0.4s ease-out;
  }
`;

// ── Form card
const FormCard = styled.div`
  width: 100%;
  max-width: 480px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(168, 85, 247, 0.15);
  border-radius: 24px;
  padding: clamp(2rem, 4vw, 2.8rem);
  position: relative;
  overflow: hidden;
  opacity: 0;
  transform: translateY(30px);

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(168, 85, 247, 0.5),
      transparent
    );
  }
`;

const FormTitle = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.7rem;
  font-weight: 500;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.25);
  margin-bottom: 1.5rem;
  text-align: center;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.9rem;
  margin-bottom: 0.9rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const FieldWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  ${({ full }) => full && "grid-column: 1 / -1;"}
`;

const FieldLabel = styled.label`
  font-family: "DM Sans", sans-serif;
  font-size: 0.62rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.28);
`;

const fieldBase = `
  width: 100%;
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  font-family: 'DM Sans', sans-serif;
  font-size: 0.9rem;
  font-weight: 300;
  color: rgba(255,255,255,0.85);
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease, box-shadow 0.2s ease;
  -webkit-appearance: none;

  &::placeholder { color: rgba(255,255,255,0.15); }

  &:focus {
    border-color: rgba(168,85,247,0.5);
    background: rgba(168,85,247,0.05);
    box-shadow: 0 0 0 3px rgba(168,85,247,0.08);
  }
`;

const Input = styled.input`
  ${fieldBase}
`;

const Select = styled.select`
  ${fieldBase}
  cursor: pointer;
  option {
    background: #0d0020;
    color: rgba(255, 255, 255, 0.85);
  }
`;

const SubmitBtn = styled.button`
  width: 100%;
  height: 50px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-family: "DM Sans", sans-serif;
  font-size: 0.88rem;
  font-weight: 500;
  letter-spacing: 0.08em;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #a855f7, #c026d3);
  background-size: 200% auto;
  box-shadow: 0 8px 30px rgba(124, 58, 237, 0.4);
  transition:
    transform 0.2s,
    box-shadow 0.2s,
    background-position 0.4s;
  margin-top: 0.9rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 12%;
    right: 12%;
    height: 1px;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.35),
      transparent
    );
  }

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 14px 40px rgba(124, 58, 237, 0.5);
    background-position: right center;
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  display: inline-block;
  vertical-align: middle;
  margin-right: 8px;
`;

const ErrorMsg = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.78rem;
  color: #f87171;
  text-align: center;
  margin-top: 0.75rem;
`;

const PrivacyNote = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.18);
  text-align: center;
  margin-top: 1rem;
  line-height: 1.5;
`;

// ── Success state
const SuccessWrap = styled.div`
  width: 100%;
  max-width: 480px;
  text-align: center;
  animation: ${countUp} 0.5s ease-out;
`;

const SuccessIcon = styled.div`
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(
    135deg,
    rgba(168, 85, 247, 0.15),
    rgba(232, 121, 249, 0.1)
  );
  border: 1px solid rgba(168, 85, 247, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.8rem;
  margin: 0 auto 1.5rem;
  animation:
    ${checkPop} 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) both,
    ${float} 4s ease-in-out 0.5s infinite;
`;

const SuccessTitle = styled.h2`
  font-family: "Playfair Display", serif;
  font-size: clamp(1.8rem, 4vw, 2.8rem);
  font-weight: 400;
  font-style: italic;
  color: #fff;
  margin-bottom: 0.8rem;
`;

const SuccessPosition = styled.div`
  font-family: "Playfair Display", serif;
  font-size: clamp(3.5rem, 8vw, 6rem);
  font-weight: 700;
  line-height: 1;
  margin-bottom: 0.5rem;
  background: linear-gradient(135deg, #e879f9, #a855f7, #7c3aed, #e879f9);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: ${shimmer} 4s linear infinite;
`;

const SuccessPositionLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: 0.75rem;
  font-weight: 400;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 1.5rem;
`;

const SuccessBody = styled.p`
  font-family: "DM Sans", sans-serif;
  font-size: 0.92rem;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.4);
  line-height: 1.75;
  max-width: 360px;
  margin: 0 auto;
`;

// ── Share row
const ShareRow = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: center;
  margin-top: 1.8rem;
  flex-wrap: wrap;
`;

const ShareBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 16px;
  height: 38px;
  border-radius: 100px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.04);
  font-family: "DM Sans", sans-serif;
  font-size: 0.72rem;
  font-weight: 400;
  color: rgba(255, 255, 255, 0.5);
  text-decoration: none;
  cursor: pointer;
  transition:
    background 0.2s,
    border-color 0.2s,
    color 0.2s;

  &:hover {
    background: rgba(168, 85, 247, 0.1);
    border-color: rgba(168, 85, 247, 0.3);
    color: rgba(255, 255, 255, 0.8);
  }
`;

// ── Admin link at bottom
const AdminLink = styled.a`
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  z-index: 50;
  font-family: "DM Sans", sans-serif;
  font-size: 0.65rem;
  font-weight: 400;
  letter-spacing: 0.1em;
  color: rgba(255, 255, 255, 0.15);
  text-decoration: none;
  transition: color 0.2s;

  &:hover {
    color: rgba(255, 255, 255, 0.4);
  }
`;

// ── Main Component
export default function WaitlistPage() {
  const heroRef = useRef(null);
  const eyebrowRef = useRef(null);
  const titleRef = useRef(null);
  const subRef = useRef(null);
  const counterRef = useRef(null);
  const formRef = useRef(null);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    state: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [position, setPosition] = useState(null);
  const [totalCount, setTotal] = useState(null);
  const [error, setError] = useState("");

  // Don't fetch count on mount — requires read permission unauthenticated users don't have.
  // We'll set totalCount after a successful signup instead.

  // Entrance animations
  useEffect(() => {
    gsap
      .timeline({ delay: 0.1 })
      .to(
        eyebrowRef.current,
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
        0,
      )
      .to(
        titleRef.current,
        { opacity: 1, y: 0, duration: 0.8, ease: "power3.out" },
        0.1,
      )
      .to(
        subRef.current,
        { opacity: 1, y: 0, duration: 0.6, ease: "power2.out" },
        0.25,
      )
      .to(
        counterRef.current,
        { opacity: 1, y: 0, duration: 0.5, ease: "power2.out" },
        0.35,
      )
      .to(
        formRef.current,
        { opacity: 1, y: 0, duration: 0.7, ease: "power3.out" },
        0.45,
      );
  }, []);

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.state) {
      setError("Please fill in all fields.");
      return;
    }
    setLoading(true);
    setError("");

    try {
      // Write the document first — position is set server-side via a counter
      // We store a placeholder and let Firestore assign the doc, then use
      // the createdAt ordering. For the displayed position we use a local
      // incrementing value stored in the doc itself.
      const docRef = await addDoc(collection(db, "waitlist"), {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        state: form.state,
        createdAt: serverTimestamp(),
      });

      // Use the auto-generated doc ID suffix as a proxy for position display
      // The real position is assigned by admin — show a friendly estimate
      const friendlyPos = Math.floor(Date.now() / 1000) % 10000;

      setPosition(friendlyPos);
      setTotal(friendlyPos);
      setSuccess(true);
    } catch (err) {
      console.error(err);
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const tweetText = encodeURIComponent(
    `Just joined the NudR waitlist 🔥 I'm #${position} — get your spot before it's gone.`,
  );

  return (
    <>
      <GlobalStyle />
      <Page>
        <Grain />
        <Orb
          s="600px"
          c="rgba(124,58,237,0.2)"
          top="-10%"
          left="-10%"
          op={0.2}
          dur="6s"
          del="0s"
          blur="130px"
        />
        <Orb
          s="500px"
          c="rgba(232,121,249,0.15)"
          bottom="-5%"
          right="-8%"
          op={0.18}
          dur="7s"
          del="1.5s"
          blur="120px"
        />
        <Orb
          s="350px"
          c="rgba(168,85,247,0.18)"
          top="40%"
          left="40%"
          op={0.12}
          dur="5.5s"
          del="0.5s"
          blur="100px"
        />

        <Hero ref={heroRef}>
          <HeroEyebrow ref={eyebrowRef}>Early access</HeroEyebrow>

          <HeroTitle ref={titleRef}>
            Be <em>bold.</em>
            <br />
            Be first.
          </HeroTitle>

          <HeroSub ref={subRef}>
            to be notified when NudR launches in your area!
          </HeroSub>

          {/* <CounterBadge
            ref={counterRef}
            style={{ visibility: totalCount !== null ? "visible" : "hidden" }}
          >
            <CounterDot />
            <CounterText>
              <strong>{totalCount?.toLocaleString() ?? "—"}</strong> people
              already on the list
            </CounterText>
          </CounterBadge> */}

          {success ? (
            <SuccessWrap>
              <SuccessIcon>🔥</SuccessIcon>
              <SuccessTitle>You're on the list.</SuccessTitle>
              {/* <SuccessPosition>#{position?.toLocaleString()}</SuccessPosition> */}
              {/* <SuccessPositionLabel>Your spot in line</SuccessPositionLabel> */}
              <SuccessBody>
                to be notified when NudR launches in your area!
              </SuccessBody>
              {/* <ShareRow>
                <ShareBtn
                  href={`https://twitter.com/intent/tweet?text=${tweetText}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  𝕏 Share on X
                </ShareBtn>
                <ShareBtn
                  href={`https://www.instagram.com/`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  📸 Instagram
                </ShareBtn>
              </ShareRow> */}
            </SuccessWrap>
          ) : (
            <FormCard ref={formRef}>
              <FormTitle>Reserve your spot</FormTitle>
              <form onSubmit={handleSubmit}>
                <FormGrid>
                  <FieldWrap>
                    <FieldLabel htmlFor="name">Full name</FieldLabel>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Jane Smith"
                      value={form.name}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap>
                    <FieldLabel htmlFor="phone">Phone number</FieldLabel>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+1 555 000 0000"
                      value={form.phone}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap full>
                    <FieldLabel htmlFor="email">Email address</FieldLabel>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="jane@email.com"
                      value={form.email}
                      onChange={handleChange}
                      required
                    />
                  </FieldWrap>
                  <FieldWrap full>
                    <FieldLabel htmlFor="state">State</FieldLabel>
                    <Select
                      id="state"
                      name="state"
                      value={form.state}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select your state</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </FieldWrap>
                </FormGrid>

                {error && <ErrorMsg>{error}</ErrorMsg>}

                <SubmitBtn type="submit" disabled={loading}>
                  {loading && <Spinner />}
                  {loading ? "Saving your spot..." : "Join the waitlist →"}
                </SubmitBtn>

                <PrivacyNote>
                  Your info is encrypted and never sold. Ever.
                </PrivacyNote>
              </form>
            </FormCard>
          )}
        </Hero>

        <AdminLink href="/admin">admin</AdminLink>
      </Page>
    </>
  );
}
